import React, { useState, useEffect } from "react";
import { Link, useNavigate, Outlet, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  LayoutDashboard, Briefcase, DollarSign, LogOut, Menu, X, ChevronRight, Package, FileText
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { useTheme } from "../../contexts/ThemeContext";
import { PushNotificationProvider } from "../../contexts/PushNotificationContext";
import { TechPermissionsProvider } from "../../contexts/TechPermissionsContext";
import TapToPayBoot from "../../components/payments/TapToPayAwareness";
import useTechLocationSharing, { resetSharingState } from "../../hooks/useTechLocationSharing";
import { LocateFixed } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TechLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [techUser, setTechUser] = useState(null);
  const [customLogo, setCustomLogo] = useState(null);
  const isNativeApp = !!(window.ReactNativeWebView || localStorage.getItem("is_native_app") === "true");
  const { enabled: sharingOn, loaded: sharingLoaded } = useTechLocationSharing();

  useEffect(() => {
    const user = localStorage.getItem("tech_user");
    const token = localStorage.getItem("tech_token");
    if (!user || !token) {
      // Check if there's a regular user session for a technician
      const regularUser = localStorage.getItem("user");
      const regularToken = localStorage.getItem("token");
      if (regularUser && regularToken) {
        try {
          const parsed = JSON.parse(regularUser);
          if (parsed.role === "technician") {
            // Migrate session
            localStorage.setItem("tech_token", regularToken);
            localStorage.setItem("tech_user", JSON.stringify({
              member_id: parsed.user_id,
              name: parsed.name,
              email: parsed.email,
              role: "technician",
              business_user_id: parsed.owner_id || "",
            }));
            localStorage.setItem("auth_kind", "tech"); // marker for silent-refresh interceptor
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            setTechUser({
              member_id: parsed.user_id,
              name: parsed.name,
              email: parsed.email,
              role: "technician",
              business_user_id: parsed.owner_id || "",
            });
            if (parsed.owner_id) fetchCustomLogo(parsed.owner_id);
            return;
          }
        } catch { /* invalid JSON */ }
      }
      navigate("/tech/login");
      return;
    }
    const parsedUser = JSON.parse(user);
    setTechUser(parsedUser);
    if (parsedUser.business_user_id) fetchCustomLogo(parsedUser.business_user_id);
  }, [navigate]);

  const fetchCustomLogo = async (userId) => {
    try {
      const res = await axios.get(`${API}/api/settings/tech-logo/${userId}`);
      if (res.headers['content-type']?.includes('image')) {
        setCustomLogo(`${API}/api/settings/tech-logo/${userId}?t=${Date.now()}`);
      }
    } catch { /* use default */ }
  };

  const handleLogout = () => {
    resetSharingState();
    localStorage.removeItem("tech_token");
    localStorage.removeItem("tech_user");
    toast.success("Logged out successfully");
    navigate("/tech/login");
  };

  const navItems = [
    { path: "/tech/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/tech/jobs", icon: Briefcase, label: "My Jobs" },
    { path: "/tech/documents", icon: FileText, label: "Docs" },
    { path: "/tech/stock", icon: Package, label: "My Stock" },
    { path: "/tech/financials", icon: DollarSign, label: "Financials" },
  ];

  const logoSrc = customLogo || (isDark ? "/logo.png" : "/logo-dark.png");
  if (!techUser) return null;

  // Build user object for PushNotificationProvider
  // Tech's member_id IS their user_id in the push notification system
  const pushUser = techUser ? { user_id: techUser.member_id, ...techUser } : null;  const getPageTitle = () => {
    if (location.pathname.includes("documents")) return "Estimates & Invoices";
    if (location.pathname.includes("jobs")) return "My Jobs";
    if (location.pathname.includes("stock")) return "My Stock";
    if (location.pathname.includes("financials")) return "Financials";
    if (location.pathname.includes("profile")) return "My Profile";
    return "Dashboard";
  };

  return (
    <PushNotificationProvider user={pushUser}>
    <TechPermissionsProvider>
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#06060b] dark' : 'bg-gray-50 light'}`} data-testid="tech-layout">
      {/* Mobile Header - hidden when inside native app */}
      {!isNativeApp && (
      <div className={`lg:hidden fixed top-0 left-0 right-0 h-14 z-50 backdrop-blur-xl ${isDark ? 'bg-[#06060b]/95 border-b border-white/5' : 'bg-white/95 border-b border-gray-200'}`}>
        <div className="flex items-center justify-between px-4 h-full">
          <button onClick={() => setSidebarOpen(true)} className={`transition-colors ${isDark ? 'text-white/70 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`} data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </button>
          <span className={`text-sm font-semibold font-['Manrope'] tracking-wide ${isDark ? 'text-white/90' : 'text-gray-900'}`}>{getPageTitle()}</span>
          <div className="flex items-center gap-2">
            {sharingLoaded && sharingOn && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/25" data-testid="sharing-indicator">
                <LocateFixed className="w-3 h-3 text-green-500" />
                <span className="text-[10px] font-medium text-green-500">Sharing</span>
              </span>
            )}
            <img src={logoSrc} alt="Logo" className="h-7 object-contain" />
          </div>
        </div>
      </div>
      )}

      {/* Native app sharing indicator (no header there) */}
      {isNativeApp && sharingLoaded && sharingOn && (
        <div className="fixed top-2 right-2 z-[60] flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/15 border border-green-500/30 backdrop-blur-sm" data-testid="sharing-indicator-native">
          <LocateFixed className="w-3 h-3 text-green-500" />
          <span className="text-[10px] font-medium text-green-500">Sharing location</span>
        </div>
      )}

      {/* Mobile Bottom Navigation - hidden when inside native app */}
      {!isNativeApp && (
      <div className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 safe-area-bottom backdrop-blur-xl ${isDark ? 'bg-[#06060b]/95 border-t border-white/5' : 'bg-white/95 border-t border-gray-200'}`}>
        <nav className="flex items-center justify-around py-2 px-4" data-testid="tech-bottom-nav">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className="relative flex flex-col items-center gap-1 px-5 py-2 transition-all duration-300"
              >
                {isActive && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-[#FFCC00]" style={{boxShadow: '0 0 8px rgba(255,204,0,0.6)'}} />
                )}
                <item.icon className={`w-5 h-5 transition-colors duration-300 ${isActive ? "text-[#FFCC00]" : isDark ? "text-white/40" : "text-gray-400"}`} />
                <span className={`text-[10px] font-medium transition-colors duration-300 ${isActive ? "text-[#FFCC00]" : isDark ? "text-white/40" : "text-gray-400"}`}>{item.label}</span>
              </Link>
            );
          })}
          <button onClick={handleLogout} className={`flex flex-col items-center gap-1 px-5 py-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-testid="nav-logout">
            <LogOut className="w-5 h-5" />
            <span className="text-[10px] font-medium">Logout</span>
          </button>
        </nav>
      </div>
      )}

      {/* Sidebar - Desktop */}
      <aside className={`fixed inset-y-0 left-0 w-[260px] z-50 transform transition-transform duration-300 ease-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} ${isDark ? 'bg-[#0c0c14] border-r border-white/5' : 'bg-white border-r border-gray-200'}`}>
        <div className="flex flex-col h-full">
          {/* Logo Area */}
          <div className={`px-6 py-5 flex items-center justify-between border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
            <img src={logoSrc} alt="Logo" className="h-9 object-contain" />
            <button onClick={() => setSidebarOpen(false)} className={`lg:hidden transition-colors ${isDark ? 'text-white/50 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}>
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User Card → Profile */}
          <Link
            to="/tech/profile"
            onClick={() => setSidebarOpen(false)}
            data-testid="sidebar-profile-card"
            className={`block mx-4 mt-4 mb-6 p-3 rounded-xl transition-colors ${isDark ? 'bg-white/5 border border-white/10 hover:bg-white/10' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm text-black" style={{background: 'linear-gradient(135deg, #FFCC00 0%, #E6B800 100%)'}}>
                {techUser.name?.charAt(0)?.toUpperCase() || "T"}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-semibold text-sm truncate font-['Manrope'] ${isDark ? 'text-white' : 'text-gray-900'}`}>{techUser.name}</p>
                <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Field Technician</p>
              </div>
              <ChevronRight className={`w-4 h-4 ${isDark ? 'text-white/30' : 'text-gray-300'}`} />
            </div>
          </Link>

          {/* Navigation */}
          <nav className="flex-1 px-3 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={`group flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                    isActive
                      ? "bg-[#FFCC00]/10 text-[#FFCC00]"
                      : isDark
                        ? "text-white/50 hover:text-white/80 hover:bg-white/[0.03]"
                        : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  <item.icon className="w-[18px] h-[18px]" />
                  <span className="font-medium text-sm font-['Manrope']">{item.label}</span>
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto opacity-50" />}
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 mt-auto">
            <div className={`h-px mb-4 ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} />
            <Button
              onClick={handleLogout}
              variant="ghost"
              data-testid="sidebar-logout"
              className={`w-full justify-start rounded-xl h-11 ${isDark ? 'text-white/40 hover:text-red-400 hover:bg-red-500/5' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}
            >
              <LogOut className="w-4 h-4 mr-3" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <main className={`lg:ml-[260px] ${isNativeApp ? 'pt-2 pb-2' : 'pt-14 pb-20'} lg:pt-0 lg:pb-0 min-h-screen`}>
        <div className="p-4 sm:p-5 lg:p-8 max-w-6xl">
          <Outlet />
          <TapToPayBoot kind="tech" />
        </div>
      </main>
    </div>
    </TechPermissionsProvider>
    </PushNotificationProvider>
  );
};

export default TechLayout;
